import type React from 'react';

import { Alert, Button, DropdownMenu, Typography } from '@cobliteam/ui/components';

import { type MediaRequest, MediaType } from '@cobliteam/common-types';

import { Analytics } from '@cobliteam/analytics';

import { useGetMediaView } from '@cobliteam/hooks/src/useCamera/useCameraMedia';

import { CircleDivider, ContentLoader } from '@cobliteam/base-components';

import Icon from '@cobliteam/ui/icons';

import useMediaStore from '../../../../store/zustand/useMediaStore';
import MediaFavoriteButton from '../../../MediaFavoriteButton';
import ShareMediaMenuItem from '../../../ShareMediaMenuItem';

export interface MediaDialogBodyProps {
  isFetchingMediaRows?: boolean;
}

const MediaDialogBody: React.FC<MediaDialogBodyProps> = ({ isFetchingMediaRows }) => {
  const mediaViewId = useMediaStore((state) => state.mediaViewId);

  const { data: mediaView } = useGetMediaView(mediaViewId);

  const mediaData = mediaView?.mediaInfo;

  const downloadVideo = async () => {
    if (!mediaView?.videos) return;

    Analytics.track('Media Download Button Clicked', {
      mediaId: mediaView.mediaInfo.id,
    });

    try {
      for (const video of mediaView.videos) {
        const response = await fetch(video.url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${mediaView.mediaInfo.mediaType}_${mediaView.mediaInfo.licensePlate}_${video.channel}.mp4`;

        link.click();

        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
      Alert.success('Vídeo baixado com sucesso!');
    } catch (error) {
      console.error('Error fetching the file:', error);
      Alert.error('Erro ao baixar o vídeo');
    }
  };

  if (!mediaData || isFetchingMediaRows) {
    return (
      <div className='flex p-5 align-middle'>
        <div className='flex space-x-3 align-middle'>
          <Typography className='self-center' variant='description-list-medium' color='secondary'>
            Esse vídeo foi interessante?
          </Typography>
          <ContentLoader key={0} width={132} height={16}>
            <rect x='0' y='0' width='132' height='16' />
          </ContentLoader>
          <ContentLoader key={1} width={132} height={16}>
            <rect x='0' y='0' width='132' height='16' />
          </ContentLoader>
        </div>
      </div>
    );
  }

  const { mediaType } = mediaData;
  const mediaDetails = mediaData?.mediaDetails as MediaRequest;

  const mediaCategory = mediaType === MediaType.TIMELAPSE ? 'resumo do trajeto' : 'vídeo de um trecho';
  const isEventVideo = mediaData && mediaData.mediaType === MediaType.EVENT_VIDEO;

  const renderVideoInfo = () => {
    if (!isEventVideo) {
      return (
        <>
          <CircleDivider className='self-center' />
          <div className='flex space-x-1 self-center'>
            <Typography variant='description-list-medium' color='secondary'>
              Categoria:
            </Typography>
            <Typography variant='description-list-default' color='secondary'>
              {mediaCategory}
            </Typography>
          </div>
          {mediaDetails?.createdBy?.fullName ? (
            <>
              <CircleDivider className='self-center' />
              <div className='flex w-[170px] space-x-1 self-center'>
                <Typography variant='description-list-medium' color='secondary'>
                  Solicitante:
                </Typography>
                <Typography variant='description-list-default' color='secondary' className='line-clamp-1'>
                  {mediaDetails.createdBy.fullName}
                </Typography>
              </div>
            </>
          ) : undefined}
        </>
      );
    }
  };
  return (
    <div className='flex p-5 align-middle'>
      <div className='flex space-x-3 align-middle'>
        <Typography className='self-center' variant='description-list-medium' color='secondary'>
          Esse vídeo foi interessante?
        </Typography>
        <MediaFavoriteButton mediaId={mediaData.id} isFavoriteCurrentState={mediaData.isFavorite} />
        <Button onClick={downloadVideo} variant='outlined' color='default' size='condensed' startIcon={<Icon name='arrow-down-to-line' />}>
          Baixar
        </Button>
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button variant='outlined' color='default' size='condensed' startIcon={<Icon name='forward' />}>
              Compartilhar
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align='end'>{mediaData && <ShareMediaMenuItem media={mediaData} />}</DropdownMenu.Content>
        </DropdownMenu>
        {renderVideoInfo()}
      </div>
    </div>
  );
};

export default MediaDialogBody;
